import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Layers, Check } from "lucide-react";
import { toast } from "sonner";
import { imgUrl } from "@/lib/api";
import { useShortlist } from "@/context/ShortlistContext";
import { Eyebrow, Reveal } from "./Reveal";

const SLOTS = [["Ovens", "Oven"], ["Cooktops", "Cooktop"], ["Rangehoods", "Rangehood"]];
const WIDTHS = ["60", "90"];
const fits = (item, w) => new RegExp(`\\b${w}\\s?cm`, "i").test(item.name);

const Slot = ({ label, options, index, setIndex }) => {
  const item = options[index];
  const step = (d) => setIndex((index + d + options.length) % options.length);
  return (
    <div className="min-w-0 border border-[#D4AF37]/20 bg-[#0D0D11]" data-testid={`package-slot-${label.toLowerCase()}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-[#13141C]">
        <AnimatePresence initial={false}>
          {item && (
            <motion.img key={item.id} src={imgUrl(item.images?.[0])} alt={item.name}
              initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
              className="absolute inset-0 w-full h-full object-cover" />
          )}
        </AnimatePresence>
        <span className="absolute top-3 left-3 font-mono-j text-[11px] uppercase tracking-[0.2em] text-[#E5C158] bg-[#0D0D11]/75 px-2.5 py-1">{label}</span>
        {options.length > 1 && (
          <div className="absolute bottom-3 right-3 flex gap-2">
            {[[-1, ChevronLeft, "prev"], [1, ChevronRight, "next"]].map(([d, Icon, n]) => (
              <button key={n} onClick={() => step(d)} aria-label={`${n} ${label}`} data-testid={`package-${label.toLowerCase()}-${n}`}
                className="w-10 h-10 grid place-items-center rounded-full bg-[#0D0D11]/75 backdrop-blur border border-[#D4AF37]/40 text-[#E5C158] hover:bg-[#D4AF37] hover:text-[#0D0D11] transition-colors">
                <Icon className="w-4 h-4" />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="p-4">
        {item ? (
          <>
            <Link to={`/item/${item.id}`} className="block font-display text-xl text-[#F5F3EF] leading-tight hover:text-[#E5C158] transition-colors">{item.name}</Link>
            <p className="mt-1 text-xs text-[#A09E97]">{item.code} · {index + 1} of {options.length}</p>
          </>
        ) : <p className="text-sm text-[#A09E97]">No {label.toLowerCase()} in this width.</p>}
      </div>
    </div>
  );
};

export const PackageBuilder = ({ items }) => {
  const { addMany } = useShortlist();
  const [width, setWidth] = useState("60");
  const [idx, setIdx] = useState({ Ovens: 0, Cooktops: 0, Rangehoods: 0 });
  const opts = Object.fromEntries(SLOTS.map(([t]) => [t, items.filter((i) => i.style === t && fits(i, width))]));
  const picks = SLOTS.map(([t]) => opts[t][idx[t]]).filter(Boolean);
  if (!items.some((i) => i.style === "Ovens")) return null;

  const add = () => {
    addMany(picks.map((p) => p.id));
    toast.success(`Added a ${width}cm ${picks.length}-piece set to your selection`);
  };

  return (
    <Reveal className="mx-auto max-w-[1440px] px-5 sm:px-10 pb-14">
      <div className="border border-[#D4AF37]/25 bg-[#13141C] p-5 sm:p-10" data-testid="package-builder">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <Eyebrow>Appliance package</Eyebrow>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl text-[#F5F3EF] leading-none">Build a matching <em className="text-[#E5C158]">set.</em></h2>
            <p className="mt-3 text-sm text-[#A09E97] max-w-md">Choose a width, then flick through ovens, cooktops and rangehoods that fit it.</p>
          </div>
          <div className="flex rounded-full border border-[#D4AF37]/30 p-1 self-start" data-testid="package-width-toggle">
            {WIDTHS.map((w) => (
              <button key={w} onClick={() => { setWidth(w); setIdx({ Ovens: 0, Cooktops: 0, Rangehoods: 0 }); }} data-testid={`package-width-${w}`}
                className={`px-6 h-10 rounded-full text-sm transition-colors ${width === w ? "bg-[#D4AF37] text-[#0D0D11]" : "text-[#A09E97] hover:text-[#F5F3EF]"}`}>{w}cm</button>
            ))}
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {SLOTS.map(([t, label]) => (
            <Slot key={t} label={label} options={opts[t]} index={Math.min(idx[t], Math.max(0, opts[t].length - 1))} setIndex={(i) => setIdx((s) => ({ ...s, [t]: i }))} />
          ))}
        </div>
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-[#A09E97] flex items-center gap-2"><Check className="w-4 h-4 text-[#D4AF37]" /> {picks.length} of 3 chosen · {width}cm set</p>
          <button onClick={add} disabled={!picks.length} data-testid="package-add-btn"
            className="h-12 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-8 text-sm font-medium text-[#0D0D11] hover:bg-[#E5C158] disabled:opacity-50 transition-colors">
            <Layers className="w-4 h-4" /> Add set to selection
          </button>
        </div>
      </div>
    </Reveal>
  );
};
